import { gql } from '@apollo/client';
import { CARRIER_INVOICE_FIELDS } from '@/graphql/queries/carrier_invoices';
import { CLAIM_ADJUSTMENT_FIELDS } from '@/graphql/fragments/claim_adjustments';
import { SUPPLEMENTAL_CHARGE_FIELDS } from '@/graphql/fragments/supplemental_charges';
import { NOTE_FIELDS } from '@/graphql/queries/notes';
import { ORDER_REFERENCE_FIELDS } from '@/graphql/fragments/order_references';
import { INTEGRATION_OUTBOUND_FIELDS } from '@/graphql/fragments/integrations_outbound';
import { PARTNERSHIP_FIELDS } from '@/graphql/fragments/partnerships';
import { INVOICE_EXPORT_ORDER_FIELDS } from '@/graphql/fragments/invoice_export_orders';

export const INVOICE_ORDER_FIELDS = gql`
    ${INTEGRATION_OUTBOUND_FIELDS}

    fragment InvoiceOrderFields on orders {
        order_id
        order_number
        po_number
        reference_id
        ready_to_invoice
        invoicing_status
        oms
        pod
        delivery_photo
        customer_signature
        order_type
        order_status
        # Scopes the charge dropdown's service catalog; the audit modal prices off this fragment.
        service_catalog_filter
        dropoff_name
        dropoff_address_confirmed
        dropoff_street
        dropoff_unit
        dropoff_city
        dropoff_state
        dropoff_zip
        pickup_street_address
        pickup_unit
        pickup_city
        pickup_state
        pickup_zip
        has_middle_mile_origin
        middle_mile_origin_city
        middle_mile_origin_state
        middle_mile_origin_zip
        billing_completion_time
        billing_completion_time_formatted @client
        delivery_date
        delivery_date_formatted @client
        shipper_id
        shipper_rate
        middle_mile_shipper_rate
        warehouse_shipper_rate
        carrier_id
        carrier_rate
        middle_mile_carrier_id
        middle_mile_carrier_rate
        warehouse_carrier_id
        warehouse_carrier_rate
        order_revenue
        price_breakdown
        order_revenue_adjustments
        shipper_rate_override_id
        middle_mile_shipper_rate_override_id
        warehouse_shipper_rate_override_id
        carrier_rate_override_id
        middle_mile_carrier_rate_override_id
        warehouse_carrier_rate_override_id
        contractor_id
        contractor_rate
        contractor_rate_override_id
        order_shipper {
            client_id
            business_name
            user {
                user_id
                organization_id
                organization {
                    organization_id
                    week_starts_on
                }
            }
        }
        shipper_rate_override {
            pricing_override_id
            marketplace_tier
        }
        order_carrier {
            client_id
            business_name
            site_code
            user {
                user_id
                organization_id
            }
        }
        order_contractor {
            client_id
            business_name
            user {
                user_id
                organization_id
            }
        }
        exceptions {
            exception {
                exception_id
                status
            }
        }
        outbound_integrations(
            where: { transaction_type: { _eq: "210_MOTOR_CARRIER_FREIGHT_DETAILS_AND_INVOICE" } }
            order_by: { status: desc }
            limit: 1
        ) {
            ...IntegrationOutboundFields
        }
    }
`;

export const GET_INVOICE = gql`
    ${CARRIER_INVOICE_FIELDS}
    ${CLAIM_ADJUSTMENT_FIELDS}
    ${SUPPLEMENTAL_CHARGE_FIELDS}
    ${NOTE_FIELDS}
    ${INVOICE_ORDER_FIELDS}
    ${ORDER_REFERENCE_FIELDS}
    ${INTEGRATION_OUTBOUND_FIELDS}
    ${PARTNERSHIP_FIELDS}
    ${INVOICE_EXPORT_ORDER_FIELDS}

    query GetInvoice($invoice_id: uuid!) {
        result: carrier_invoices_by_pk(carrier_invoice_id: $invoice_id) {
            ...CarrierInvoiceFields

            outbound_integrations(
                where: { transaction_type: { _eq: "210_MOTOR_CARRIER_FREIGHT_DETAILS_AND_INVOICE" } }
                order_by: { status: desc }
                limit: 1
            ) {
                ...IntegrationOutboundFields
            }

            notes {
                ...NoteFields
            }

            supplemental_charges {
                ...SupplementalChargeFields
            }

            claim_adjustments {
                ...ClaimAdjustmentFields
                claim {
                    claim_id
                    order {
                        order_id
                        order_number
                        po_number
                        reference_id
                        order_shipper {
                            client_id
                            business_name
                        }
                        ...InvoiceExportOrderFields
                    }
                }
            }

            client {
                client_id
                business_name
                billcom_vendor_id
                scac
                email
                billing_email
                summarized_billing
                user {
                    user_id
                    circles
                }
                shipper_partnerships {
                    ...PartnershipFields
                }
            }

            partner_client {
                client_id
                business_name
            }

            shipper_orders {
                ...InvoiceOrderFields
                # Extra fields MASTERBRAND_COLUMNS reads. Declared inline rather than in
                # InvoiceOrderFields because that fragment is also spread by CompletedOrders' 50-row
                # paged list and by UPDATE_ORDERS, and itemsByOrderId and references are nested
                # collections. The MasterBrand export only ever reads shipper_orders.
                custom_fields
                # Origin Plant ID and Channel read origin_id / channel_code from here before
                # custom_fields. The full fragment rather than key/value: order_references has no cache
                # key, so a narrower list would replace the one the order pages cache on this order.
                references {
                    ...OrderReferenceFields
                }
                received_date
                est_received_date
                itemsByOrderId {
                    item_id
                    quantity
                    item_type_details
                    intangible
                }
                ...InvoiceExportOrderFields
            }
            orders {
                ...InvoiceOrderFields
            }
            middle_mile_carrier_orders {
                ...InvoiceOrderFields
            }
            warehouse_carrier_orders {
                ...InvoiceOrderFields
            }
            contractor_orders {
                ...InvoiceOrderFields
            }
        }
    }
`;

export const UPDATE_INVOICE = gql`
    ${CARRIER_INVOICE_FIELDS}
    mutation UpdateInvoice($invoice_id: uuid!, $patch: carrier_invoices_set_input!) {
        updated: update_carrier_invoices_by_pk(pk_columns: { carrier_invoice_id: $invoice_id }, _set: $patch) {
            ...CarrierInvoiceFields
        }
    }
`;

export const INSERT_NOTES = gql`
    ${NOTE_FIELDS}
    mutation InsertNotes($notes: [notes_insert_input!]!) {
        created: insert_notes(objects: $notes) {
            affected_rows
            returning {
                ...NoteFields
            }
        }
    }
`;

export const SAVE_SUPPLEMENTAL_CHARGES = gql`
    ${SUPPLEMENTAL_CHARGE_FIELDS}
    mutation SaveSupplementalCharges($invoice_id: uuid!, $charges: [invoice_supplemental_charges_insert_input!]!) {
        deleted: delete_invoice_supplemental_charges(where: { invoice_id: { _eq: $invoice_id } }) {
            affected_rows
        }
        created: insert_invoice_supplemental_charges(objects: $charges) {
            returning {
                ...SupplementalChargeFields
            }
        }
    }
`;
